import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { isEmpty } from 'lodash';
import { Button, Divider, Space } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { useDebounce, useDefaultDeleteModal } from '@/hooks';

import {
  CreateCustomerVehicleRequest,
  CustomerVehicleReduced,
  CustomerVehiclesFilterParamsReduced,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import {
  useCreateCustomerVehicle,
  useDeleteCustomerVehicle,
  useUpdateCustomerVehicle,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import { PageProps } from '@/types/PageProps';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { ColumnsType } from '@/components/_common/Table';
import {
  useCustomerReducedQuery,
  useVehiclesFilterReduced,
} from '@/core/domain/people/Customers/Customers.hooks';
import { getTableScrollX } from '@/utils/table';

import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { AppPageHeader, InfiniteTable, SaveButton } from '@/components/_common';
import { useCustomerFormContext } from '@/components/Customers/CustomerFormProvider';
import CustomerFooter from '@/components/Customers/CustomerRepeatedButtons';
import Authorities from '@/types/Authority';
import VehicleModal from '@/components/_common/_VehicleModal';
import { VehicleReturnValues } from '@/components/_common/_VehicleModal/VehicleModal.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { CustomerVehicleCombined } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';
import { useReportHandlers } from '@/hooks/useReportHandler';
import { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useGenerateCustomerVehiclesReport } from '@/core/domain/reports/people/Report.hooks';
import VehicleActionButton from '@/components/_common/_VehicleModal/VehicleActionButton';

const CustomerVehiclesPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const { setParamLabel } = useBreadcrumbs();
  const { handleDelete } = useDefaultDeleteModal('customers.vehicle');
  const { customerId } = useRouteMatch<{ customerId: string }>().params;

  const [modalVisible, setModalVisible] = useState(false);

  const [selectedVehicle, setSelectedVehicle] =
    useState<CustomerVehicleCombined>();

  const { data: customer, isFetching: isFetchingCustomer } =
    useCustomerReducedQuery(+customerId);
  const { handleSubmit, disabled, isLoading } = useCustomerFormContext();

  const [queryParams, setQueryParams] =
    useState<CustomerVehiclesFilterParamsReduced>({
      customerId: +customerId,
      active: true,
    });

  const queryDebounce = useDebounce(queryParams);

  const vehicleList = useVehiclesFilterReduced(queryDebounce);

  const vehiclesData = flatInfiniteData(vehicleList.data);

  const createMutation = useCreateCustomerVehicle(+customerId);
  const updateMutation = useUpdateCustomerVehicle(+customerId);
  const deleteMutation = useDeleteCustomerVehicle();

  useEffect(() => {
    if (customer && !isEmpty(customer)) {
      setParamLabel(':customerId', customer.name);
    }
  }, [customer, isFetchingCustomer]);

  const modalToggle = (vehicle?: CustomerVehicleReduced) => {
    setSelectedVehicle(vehicle);
    setModalVisible(!modalVisible);
  };

  const handleVehicleModalOk = async (values: VehicleReturnValues) => {
    let image = values.image;

    if (!!values.imageUpload) {
      const result = await uploadArchive({
        file: [values.imageUpload as Blob],
        isPublic: false,
      });

      image = result.filePath;
    }

    const obj: CreateCustomerVehicleRequest = {
      model: {
        id: values.modelId,
        brand: { id: values.brandId, image: values.model?.brand.image },
      },
      fuel: { id: values.fuelId },
      color: { id: values.colorId },
      licensePlate: values.licensePlate,
      yearFabrication: Number(values.yearFabrication),
      yearModel: Number(values.yearModel),
      chassi: values.chassi,
      fleet: values.fleet,
      km: values.km,
      averageKm: values.averageKm,
      renavam: values.renavam,
      type: values?.type,
      active: true,
      accessories: values.accessories || [],
      image,
      cylinders: values.cylinders,
      engineCapacity: values.engineCapacity,
      engineName: values.engineName,
      engineTechnicalCode: values.engineTechnicalCode,
      horsePower: values.horsePower,
      release: values.release,
      valves: values.valves,
      line: values.line,
      airIntake: values.airIntake,
      rearBrake: values.rearBrake,
      frontBrake: values.frontBrake,
      brakeSystem: values.brakeSystem,
      steeringSystem: values.steeringSystem,
      tractionSystem: values.tractionSystem,
      injectionSystem: values.injectionSystem,
      transmissionType: values.transmissionType,
      engineConfiguration: values.engineConfiguration,
    };

    if (!selectedVehicle) {
      await createMutation.mutateAsync(obj);
    } else {
      await updateMutation.mutateAsync({ ...obj, id: selectedVehicle.id });
    }

    vehicleList.refetch();

    modalToggle();
  };

  const generateCustomerVehiclesReport = useGenerateCustomerVehiclesReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    if (format === 'CSV') {
      const result = await generateCustomerVehiclesReport.mutateAsync(
        Number(customerId),
      );

      if (callback) {
        callback(result);
      }
    }
  };

  const { handleCsv } = useReportHandlers(
    'relatorio_veículos_por_cliente',
    handleGenerateReport,
  );

  const columns: ColumnsType<CustomerVehicleReduced> = [
    {
      dataIndex: 'licensePlate',
      title: <FormattedMessage id="customers.domain.vehicle.licensePlate" />,
      width: 100,
    },
    {
      dataIndex: 'brandName',
      title: <FormattedMessage id="customers.domain.vehicle.brand" />,
      width: 100,
    },
    {
      dataIndex: 'modelName',
      title: <FormattedMessage id="customers.domain.vehicle.model" />,
      width: 100,
      render: (modelName) => (
        <EllipsisHoverToggle>{modelName}</EllipsisHoverToggle>
      ),
    },
    {
      dataIndex: 'yearFabrication',
      title: <FormattedMessage id="customers.domain.vehicle.yearFabrication" />,
      width: 100,
    },
    {
      dataIndex: 'yearModel',
      title: <FormattedMessage id="customers.domain.vehicle.yearModel" />,
      width: 100,
    },
    {
      title: <FormattedMessage id="customers.list.header.actions" />,
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, row) => (
        <Space>
          <EditIcon iconProps={{ onClick: () => modalToggle(row) }} />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={
              !hasPermissions([Authorities.REGISTER_CUSTOMER_VEHICLE_DELETE])
            }
            onClick={() =>
              handleDelete(
                () =>
                  deleteMutation.mutateAsync({
                    customerId: +customerId,
                    vehicleId: row.id,
                  }),
                () => vehicleList.refetch(),
              )
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={route.parent}
        baseKey={`${customerId}`}
        basePath={`/app/customers/${customerId}`}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={handleCsv}
              loading={generateCustomerVehiclesReport.isLoading}
            >
              Gerar relatório de veículos
            </Button>

            <VehicleActionButton
              mode="create"
              onClick={modalToggle}
              variant="primary"
              block
              label={<FormattedMessage id="customers.domain.vehicle.create" />}
              requireCustomer={false}
            />

            <SaveButton
              block
              size="large"
              loading={isLoading}
              onClick={handleSubmit}
              disabled={
                disabled ||
                !hasPermissions([Authorities.REGISTER_CUSTOMER_EDIT])
              }
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          onSearch={(licensePlate) =>
            setQueryParams((prev) => ({ ...prev, licensePlate }))
          }
          searchPlaceholder="Placa"
        />

        <InfiniteTable<CustomerVehicleReduced>
          columns={columns}
          dataSource={vehiclesData}
          loading={vehicleList.isFetching}
          rowKey={(row) => row.id}
          fetchNextPage={vehicleList.fetchNextPage}
          hasNextPage={vehicleList.hasNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <CustomerFooter customerId={Number(customerId)} disabled={disabled} />

        <VehicleModal
          onOk={handleVehicleModalOk}
          open={modalVisible}
          onCancel={() => {
            modalToggle();
          }}
          vehicleId={selectedVehicle?.id}
          customerId={customerId ? Number(customerId) : undefined}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default CustomerVehiclesPage;
